<template>
  <div class="editor">
    <div class="editor__menu">
      <div class="editor__menu-select">
        <Select
          :value="selectedText"
          :options="selectedTextOptions"
          option-text="name"
          option-key="value"
          @input="onTextStyleChange($event)"
        />
      </div>
      <div class="editor__menu-divider" />
      <div class="editor__menu-group">
        <div
          class="editor__menu-item"
          :class="{
            'editor__menu-item--active':
              editor === null ? false : editor.isActive({ textAlign: 'left' })
          }"
          @click="editor.chain().focus().setTextAlign('left').run()"
        >
          <JustifyLeftIcon />
        </div>
        <div
          class="editor__menu-item"
          :class="{
            'editor__menu-item--active':
              editor === null ? false : editor.isActive({ textAlign: 'center' })
          }"
          @click="editor.chain().focus().setTextAlign('center').run()"
        >
          <JustifyCenterIcon />
        </div>
        <div
          class="editor__menu-item"
          :class="{
            'editor__menu-item--active':
              editor === null ? false : editor.isActive({ textAlign: 'right' })
          }"
          @click="editor.chain().focus().setTextAlign('right').run()"
        >
          <JustifyRightIcon />
        </div>
      </div>
      <div class="editor__menu-divider" />
      <div class="editor__menu-group">
        <div
          class="editor__menu-item"
          :class="{
            'editor__menu-item--active': editor === null ? false : editor.isActive('bulletList')
          }"
          @click="editor.chain().focus().toggleBulletList().run()"
        >
          <UnorderedListIcon />
        </div>
        <div
          class="editor__menu-item"
          :class="{
            'editor__menu-item--active': editor === null ? false : editor.isActive('orderedList')
          }"
          @click="editor.chain().focus().toggleOrderedList().run()"
        >
          <OrderedListIcon />
        </div>
      </div>
      <div class="editor__menu-divider" />
      <div class="editor__menu-group">
        <div
          class="editor__menu-item"
          :class="{
            'editor__menu-item--active': editor === null ? false : editor.isActive('bold')
          }"
          @click="editor.chain().focus().toggleBold().run()"
        >
          <BoldTextIcon />
        </div>
        <div
          class="editor__menu-item"
          :class="{
            'editor__menu-item--active': editor === null ? false : editor.isActive('italic')
          }"
          @click="editor.chain().focus().toggleItalic().run()"
        >
          <ItalicsTextIcon />
        </div>
        <div
          class="editor__menu-item"
          :class="{
            'editor__menu-item--active': editor === null ? false : editor.isActive('underline')
          }"
          @click="editor.chain().focus().toggleUnderline().run()"
        >
          <UnderlineTextIcon />
        </div>
        <div class="editor__menu-item">
          <ColorTextIcon />
          <div
            class="editor__color-ribbon"
            :style="{
              backgroundColor: editor === null ? '#000000' : editor.getAttributes('textStyle').color
            }"
          />
          <input
            ref="color-input"
            :value="
              editor === null
                ? '#000000'
                : editor.getAttributes('textStyle').color
                ? editor.getAttributes('textStyle').color
                : '#000000'
            "
            type="color"
            class="editor__color-input"
            list="editor-color-presets"
            @input="editor.chain().focus().setColor($event.target.value).run()"
          />
          <datalist id="editor-color-presets">
            <option>#6979F8</option>
            <option>#36383E</option>
            <option>#F39200</option>
            <option>#EB5757</option>
            <option>#209D3C</option>
          </datalist>
        </div>
      </div>
    </div>
    <div class="editor__content">
      <EditorContent :editor="editor" />
    </div>
  </div>
</template>

<script>
import { Editor, EditorContent } from '@tiptap/vue-2';
import StarterKit from '@tiptap/starter-kit';
import TextAlign from '@tiptap/extension-text-align';
import TextStyle from '@tiptap/extension-text-style';
import Color from '@tiptap/extension-color';
import Underline from '@tiptap/extension-underline';
import Select from './Select';
import JustifyLeftIcon from '@/components/Icons/JustifyLeftIcon';
import JustifyRightIcon from '@/components/Icons/JustifyRightIcon';
import JustifyCenterIcon from '@/components/Icons/JustifyCenterIcon';
import UnorderedListIcon from '@/components/Icons/UnorderedListIcon';
import OrderedListIcon from '@/components/Icons/OrderedListIcon';
import BoldTextIcon from '@/components/Icons/BoldTextIcon';
import ItalicsTextIcon from '@/components/Icons/ItalicTextIcon';
import UnderlineTextIcon from '@/components/Icons/UnderlineTextIcon';
import ColorTextIcon from '@/components/Icons/ColorTextIcon';

export default {
  name: 'ContentEditor',
  components: {
    EditorContent,
    Select,
    JustifyLeftIcon,
    JustifyRightIcon,
    JustifyCenterIcon,
    UnorderedListIcon,
    OrderedListIcon,
    BoldTextIcon,
    ItalicsTextIcon,
    UnderlineTextIcon,
    ColorTextIcon
  },
  props: {
    value: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      editor: null,
      selectedText: {
        name: 'Обычный текст',
        value: 'normal'
      },
      selectedTextOptions: [
        {
          name: 'Заголовок',
          value: 'header'
        },
        {
          name: 'Подзаголовок',
          value: 'subheader'
        },
        {
          name: 'Обычный текст',
          value: 'normal'
        }
      ]
    };
  },
  watch: {
    value(value) {
      const isSame = this.editor.getHTML() === value;
      if (!isSame) {
        this.editor.commands.setContent(value, false);
      }
    }
  },
  mounted() {
    this.editor = new Editor({
      content: this.value,
      extensions: [
        StarterKit,
        TextAlign.configure({
          types: ['heading', 'paragraph', 'listItem']
        }),
        Underline,
        TextStyle,
        Color
      ],
      onUpdate: () => {
        this.$emit('input', this.editor.getHTML());
      },
      onSelectionUpdate: () => {
        if (this.editor.isActive('heading', { level: 1 })) {
          this.selectedText = this.selectedTextOptions[0];
        } else if (this.editor.isActive('heading', { level: 3 })) {
          this.selectedText = this.selectedTextOptions[1];
        } else if (this.editor.isActive('paragraph')) {
          this.selectedText = this.selectedTextOptions[2];
        }
      }
    });
  },
  beforeDestroy() {
    this.editor.destroy();
  },
  methods: {
    onTextStyleChange(value) {
      this.selectedText = value;
      if (value.value === 'normal') {
        this.editor.chain().focus().setParagraph().unsetBold().run();
      } else if (value.value === 'subheader') {
        this.editor.chain().focus().setHeading({ level: 3 }).setBold().run();
      } else if (value.value === 'header') {
        this.editor.chain().focus().setHeading({ level: 1 }).setBold().run();
      }
    }
  }
};
</script>

<style lang="scss" scoped>
@import 'src/style/utils';

.editor {
  &__menu {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    min-height: 44px;
    justify-content: flex-start;
    align-items: center;
    background: white;
    border-radius: 4px;
    box-shadow: 0 4px 10px rgba(43, 45, 52, 0.12);
    margin-bottom: 30px;
    padding: 8px 16px;

    &-group {
      display: grid;
      grid-auto-flow: column;
      gap: 4px;
    }

    &-divider {
      height: 24px;
      width: 1px;
      background-color: #dfe0eb;
      margin: 0 12px;
    }

    &-select {
      flex: 1;
      max-width: 200px;
    }

    &-item {
      position: relative;
      border-radius: 2px;
      cursor: pointer;

      &:hover svg,
      &:hover path {
        fill: $ui-blue-accident;
      }

      &--active {
        background: #e9ebfe;
        svg,
        path {
          fill: $ui-blue-accident;
        }
      }
    }
  }

  &__color {
    &-input {
      opacity: 0;
      position: absolute;
      left: 0;
      top: 0;
      width: 100%;
      height: 100%;
      cursor: pointer;
    }

    &-ribbon {
      width: 16px;
      height: 3px;
      background-color: black;
      position: absolute;
      left: 6px;
      bottom: 8px;
    }
  }

  &__content {
    background: white;
    border-radius: 8px;
    padding: 20px;
    box-shadow: 0 4px 10px rgba(43, 45, 52, 0.12);
    width: 100%;
    max-height: 350px;
    margin-bottom: 20px;
    overflow-y: auto;

    &::v-deep ul {
      list-style-type: disc;
      list-style-position: inside;

      p,
      h1,
      h3 {
        display: inline;
      }
    }
    &::v-deep ol {
      list-style-type: decimal;
      list-style-position: inside;

      p,
      h1,
      h3 {
        display: inline;
      }
    }

    &::v-deep p,
    &::v-deep h1,
    &::v-deep h3 {
      @include uiFont;
    }

    &::v-deep p {
      font-size: 14px;
      font-weight: 400;
      margin-bottom: 8px;
    }

    &::v-deep li {
      font-size: 14px;
      font-weight: 400;
      margin-bottom: 8px;
    }

    &::v-deep h1 {
      font-size: 36px;
      font-weight: 400;
      margin-bottom: 18px;
    }

    &::v-deep h3 {
      font-size: 18px;
      font-weight: 400;
      margin-bottom: 10px;
    }
  }
}
</style>
